import { createInjectionState, useScroll } from '@vueuse/core'
import filter from 'lodash/filter'
import findIndex from 'lodash/findIndex'
import trim from 'lodash/trim'
import Store from 'store'
import { computed, nextTick, reactive, ref, shallowReactive, shallowRef, useTemplateRef } from 'vue'
import { bus } from '@/common/setup-app/event-bus.js'
import StorageKeys from '@/common/storage-keys.js'
import { get_email_from_queries } from '@/common/tracking-utils'
import { fill_utm_form } from '@/common/utm-utils.js'
import { get_queries_from_url, pagination } from '@/utils.js'

const [useProvideQuickConversation, _useQuickConversation] = createInjectionState((props, emit) => {
  const candidateResponses = computed(() => props.candidateResponses)
  const candidateResponsesClicked = shallowRef([])

  const defaultMessages = computed(() => props.defaultMessages)
  const isTempUser = ref(false)

  const isChattingWithAi = computed(() => {
    // TODO use another way to determine if chatting with AI
    return !!props.salesAssistant
  })
  const userPhotoUrl = computed(() => {
    if (isChattingWithAi.value) {
      return 'https://do.dubbcdn.com/img/sia-logo.png'
    }
    return props.user.photo_url
  })

  const userDisplayName = computed(() => {
    return isChattingWithAi.value ? 'AI Assistant' : props.user.name
  })

  const composeForm = reactive(new SparkForm({
    draft: '',
    message: '',
    from_cta: true,
    video_id: props.video ? props.video.id : null,
    hash_sales_assistant_id: props.salesAssistant.hash_id,
  }))

  const queries = get_queries_from_url()
  const signUpForm = reactive(new SparkForm({
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_content: '',
    utm_term: '',
    referrer_url: '',
    landing_url: null,
    context: {
      source: 'chat',
      video_id: props.video ? props.video.id : null,
    },
    email: get_email_from_queries(props.video ? props.video.canonical : null, queries),
    first_name: queries.first_name || null,
    last_name: queries.last_name || null,
    contact: queries.contact || null,
  }))

  const conversationMessages = shallowReactive(pagination())
  const conversationId = ref(null)

  const messagesRef = useTemplateRef('messagesRef')
  const { y, arrivedState } = useScroll(messagesRef)

  const isMessageResponding = computed(() => {
    return conversationMessages.data.some((message) => {
      return message.is_typing
    })
  })

  const shouldNotShowScrollToBottom = computed(() => {
    return arrivedState.bottom || arrivedState.top
  })

  const scrollToBottom = () => {
    nextTick(() => {
      y.value = messagesRef.value.scrollHeight
    })
  }

  const initSparkState = (state) => {
    isTempUser.value = true
    Spark.state = { ...Spark.state, ...state }
    bus.$emit('login-user', { apiMode: true })
  }

  const appendMessage = (message) => {
    const index = findIndex(conversationMessages.data, { id: message.id })
    if (index >= 0) {
      conversationMessages.data = conversationMessages.data.map((item) => {
        if (item.id === message.id) {
          return message
        }
        return item
      })
    }
    else {
      conversationMessages.data = [
        ...conversationMessages.data,
        message,
      ]
    }
    if (message.sender_id !== Spark.state.user.id) {
      if (!message.is_typing) {
        conversationMessages.data = filter(conversationMessages.data, message => !message.is_typing)
      }
    }
  }

  const requestTimes = computed(() => {
    return conversationMessages.data.filter((message) => {
      return message.sender_id === Spark.state.user.id
    }).length
  })

  const limitRequests = computed(() => {
    return props.limitRequests
  })

  const triggerLimitRequests = () => {
    emit('trigger-limit-requests')
  }

  const sendMessage = () => {
    if (props.limitRequests && requestTimes.value >= props.limitRequests && isTempUser.value) {
      triggerLimitRequests()
      return
    }
    if (Spark.state.user.id === props.user.id) {
      window.SwalConfirm.fire({
        icon: 'warning',
        title: 'You cannot chat with yourself',
        text: 'Copy and paste this URL into an incognito browser window to test your AI chat assistant.',
        showCancelButton: false,
      })
      return
    }

    composeForm.message = trim(composeForm.draft)
    if (composeForm.message === '') {
      return
    }
    composeForm.draft = ''
    Spark.post(Spark.router('api.conversation.send', { friend: props.user.id }), composeForm)
      .then((resp) => {
        composeForm.message = ''
        composeForm.draft = ''
        appendMessage(resp)
        conversationId.value = resp.conversation_id

        scrollToBottom()
      })
  }

  const signUp = async () => {
    fill_utm_form(signUpForm, {
      utm_source: 'Dubb',
      utm_medium: 'chat',
      utm_campaign: 'DubbChat',
      utm_content: 'SalesAssistant',
    })

    const referrerUrl = Store.get(StorageKeys.REFERRER_URL)

    if (referrerUrl) {
      signUpForm.referrer_url = referrerUrl
    }
    else {
      signUpForm.referrer_url = location.href
    }
    const landingUrl = Store.get(StorageKeys.LANDING_URL)
    if (landingUrl) {
      signUpForm.landing_url = landingUrl
    }
    else {
      signUpForm.landing_url = location.href
    }

    Spark.post(Spark.router('api.quick-register'), signUpForm)
      .then((resp) => {
        bus.$once('pusher-connected', () => {
          sendMessage()
        })
        sessionStorage.setItem(StorageKeys.TEMP_USER_STATE, JSON.stringify(resp))
        initSparkState(resp)
      })
  }

  const checkAndSend = () => {
    if (Spark.state.user) {
      sendMessage()
      return
    }
    signUp()
  }

  return {
    signUpForm,
    candidateResponses,
    candidateResponsesClicked,

    isChattingWithAi,
    userPhotoUrl,
    userDisplayName,

    defaultMessages,
    conversationMessages,
    isMessageResponding,

    composeForm,

    shouldNotShowScrollToBottom,
    scrollToBottom,

    appendMessage,
    initSparkState,
    checkAndSend,
    sendMessage,
    signUp,

    conversationId,

    limitRequests,
    triggerLimitRequests,
  }
})

function useQuickConversation() {
  const res = _useQuickConversation()
  if (!res) {
    throw new Error('useQuickConversation() is called without provider.')
  }
  return res
}

export {
  useProvideQuickConversation,
  useQuickConversation,
}
